// §6.3 / §14 — the macOS desktop surface: a generated wallpaper (D-043) and a right-click menu.
//
// **No icon grid.** This platform's desktop is a place files go, not a place apps live — every app
// is one Launchpad or Spotlight away, and the Dock holds the ones you keep. Copying the house
// skin's app grid here would be copying a look rather than implementing a platform.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWallpaperBackground } from '@/host/useWallpaper';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';

interface MenuState {
  x: number;
  y: number;
}

export function DesktopSurface() {
  const kernel = useKernel();
  const wallpaper = useWallpaperBackground();
  const [menu, setMenu] = useState<MenuState | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (!menu) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    const onPointer = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [menu, close]);

  return (
    <div
      className="macos-desktop"
      data-testid="desktop-surface"
      style={{ background: wallpaper }}
      onContextMenu={(event) => {
        event.preventDefault();
        setMenu({ x: event.clientX, y: event.clientY });
      }}
    >
      {menu ? (
        <div
          ref={menuRef}
          className="macos-context-menu"
          data-testid="desktop-context-menu"
          style={{ left: menu.x, top: menu.y }}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className="macos-menu-item"
            onClick={() => {
              close();
              kernel.shellui.openOverlay('launcher');
            }}
          >
            {COPY.chrome.openLauncher}
          </button>
          <button
            type="button"
            role="menuitem"
            className="macos-menu-item"
            onClick={() => {
              close();
              kernel.launch.launch('settings');
            }}
          >
            {COPY.chrome.personalize}
          </button>
        </div>
      ) : null}
    </div>
  );
}
